import React from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  MarkerType,
  getSmoothStepPath,
} from "reactflow";
import { Trash2 } from "lucide-react";

const MAX_LABEL_CHARS = 28;

const trimLabel = value => {
  const text = String(value || "").trim();
  if (!text) return "";
  if (text.length <= MAX_LABEL_CHARS) return text;
  return `${text.slice(0, MAX_LABEL_CHARS)}...`;
};

export default function AutoReplySmartEdge(props) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style,
    markerEnd = { type: MarkerType.ArrowClosed, color: "#94a3b8" },
    label,
    selected,
    onRequestDelete,
  } = props;

  // Use a smooth step line matching visual builder style
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    borderRadius: 16, // gentle curves on the step turns
  });

  const safeLabel = trimLabel(label);
  const selectedColor = "#dc2626";
  const selectedMarkerEnd =
    selected && markerEnd && typeof markerEnd === "object"
      ? { ...markerEnd, color: selectedColor }
      : markerEnd;

  return (
    <g className="ar-smart-edge group">
      <BaseEdge
        id={id}
        path={edgePath}
        interactionWidth={24}
        markerEnd={selectedMarkerEnd}
        className="transition-all duration-200"
        style={{
          ...style,
          stroke: selected ? selectedColor : "#94a3b8",
          strokeWidth: selected ? 2.5 : 2,
          strokeDasharray: "5, 5", // Dashed line effect
          opacity: selected ? 1 : 0.9,
          zIndex: 1000, // Keep edges above standard nodes
        }}
      />

      {(safeLabel || selected) && (
        <EdgeLabelRenderer>
          <div
            className="pointer-events-none"
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              zIndex: 1001,
            }}
          >
            <div className="flex items-center gap-1.5">
              {safeLabel && (
                <div
                  title={String(label || "")}
                  className="pointer-events-none bg-white border border-slate-200 rounded-full px-2.5 py-1 text-[10px] font-medium text-slate-600 shadow-sm"
                  style={{
                    maxWidth: 180,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {safeLabel}
                </div>
              )}
              {selected && (
                <button
                  type="button"
                  aria-label="Delete connection"
                  title="Delete connection"
                  className="nodrag nopan pointer-events-auto flex h-6 w-6 items-center justify-center rounded-full border border-red-200 bg-white text-red-600 shadow-sm hover:bg-red-50"
                  onPointerDown={event => {
                    event.stopPropagation();
                    onRequestDelete?.(id);
                  }}
                  onMouseDown={event => event.stopPropagation()}
                  onClick={event => {
                    event.stopPropagation();
                    if (event.detail === 0) {
                      onRequestDelete?.(id);
                    }
                  }}
                >
                  <Trash2 size={13} strokeWidth={2.25} />
                </button>
              )}
            </div>
          </div>
        </EdgeLabelRenderer>
      )}
    </g>
  );
}
